import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Filter, UserPlus, Star, Ban, Building2, Users, Repeat,
  X, Save,
} from "lucide-react";
import { guestAPI, VIP_STYLE, cls, fmtDT, fmtINR, initials, fmtD } from "./guestApi";
import GuestProfileDrawer from "./GuestProfileDrawer";
import { DatePicker } from "@/components/ui/date-picker";
import { FormField, errorRing } from "@/components/form/FormField";
import { useFormValidation } from "@/hooks/useFormValidation";
import { required, email as emailV, phone as phoneV } from "@/lib/validators";

const FILTER_CHIPS = [
  { k: "vip",       label: "VIP",       icon: Star },
  { k: "corporate", label: "Corporate", icon: Building2 },
  { k: "repeat",    label: "Repeat",    icon: Repeat },
  { k: "current",   label: "In-House",  icon: Users },
  { k: "blacklist", label: "Blacklist", icon: Ban },
];

export default function GuestDirectory() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = () => guestAPI.list({
    q: q || undefined,
    vip: !!filters.vip || undefined,
    corporate: !!filters.corporate || undefined,
    repeat: !!filters.repeat || undefined,
    current: !!filters.current || undefined,
    blacklist: !!filters.blacklist || undefined,
    limit: 200,
  }).then(setRows).catch(()=>{});

  useEffect(() => {
    const t = setTimeout(load, 250); return () => clearTimeout(t);
  }, [q, filters]);   // eslint-disable-line

  useEffect(() => {
    if (sp.get("new") === "1")       { setCreating(true); sp.delete("new"); setSp(sp, { replace: true }); }
    if (sp.get("vip") === "1")       setFilters(f => ({...f, vip: true}));
    if (sp.get("corporate") === "1") setFilters(f => ({...f, corporate: true}));
    if (sp.get("repeat") === "1")    setFilters(f => ({...f, repeat: true}));
    if (sp.get("blacklist") === "1") setFilters(f => ({...f, blacklist: true}));
  }, []);   // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="guest-directory">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)}
                 placeholder="Search name, email, phone, passport, company…" aria-label="Search guests"
                 data-testid="guest-directory-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-96 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1" data-testid="guest-directory-filters">
          {FILTER_CHIPS.map(f => {
            const I = f.icon;
            return (
              <button key={f.k} onClick={()=>setFilters(x => ({...x, [f.k]: !x[f.k]}))}
                      data-testid={`guest-directory-filter-${f.k}`}
                      className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border inline-flex items-center gap-1.5 ${
                        filters[f.k] ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                <I className="w-3.5 h-3.5"/>{f.label}
              </button>
            );
          })}
        </div>
        <button onClick={()=>setCreating(true)} data-testid="guest-directory-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <UserPlus className="w-4 h-4"/> New Guest
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {rows.length === 0
          ? <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-2"/>
              <div className="text-[13px] text-slate-500">No guests match this filter.</div>
            </div>
          : (
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Guest</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-center px-4 py-3">Tier</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-right px-4 py-3">Stays</th>
                  <th className="text-right px-4 py-3">Lifetime</th>
                  <th className="text-center px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(g => (
                  <tr key={g.id} onClick={()=>setOpenId(g.id)}
                      data-testid={`guest-row-${g.id}`}
                      className={`cursor-pointer hover:bg-slate-50/70 ${g.blacklist?.active ? "bg-rose-50/40" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-hocp-navy text-white text-[11px] font-semibold flex items-center justify-center">
                          {initials(g)}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-hocp-navy truncate">
                            {g.first_name} {g.last_name || ""}
                            {g.blacklist?.active && <Ban className="w-3.5 h-3.5 text-rose-500 inline ml-1"/>}
                          </div>
                          <div className="text-[11px] text-slate-500 mono truncate max-w-xs">{g.nationality || "—"}{g.dob ? ` · DOB ${fmtD(g.dob)}` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[12px] text-slate-700">{g.email || "—"}</div>
                      <div className="text-[11px] text-slate-500 mono">{g.phone || ""}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${VIP_STYLE[g.vip_level] || VIP_STYLE.standard}`}>{g.vip_level}</span>
                    </td>
                    <td className="px-4 py-3 text-[12px] text-slate-600 truncate max-w-[140px]">{g.company_name || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-mono font-medium">{g.stats?.stays || 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-medium">{fmtINR(g.stats?.revenue || 0)}</td>
                    <td className="px-4 py-3 text-center">
                      {g.stats?.is_current
                        ? <span className="px-2 py-0.5 rounded-md text-[10.5px] font-medium bg-emerald-100 text-emerald-700">In-House</span>
                        : g.stats?.is_repeat
                          ? <span className="px-2 py-0.5 rounded-md text-[10.5px] font-medium bg-sky-100 text-sky-700">Repeat</span>
                          : <span className="text-[10.5px] text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[11.5px] mono text-slate-400">{fmtDT(g.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      {creating && <NewGuestDialog onClose={()=>setCreating(false)} onDone={g=>{ setCreating(false); load(); setOpenId(g.id); }}/>}
      {openId && <GuestProfileDrawer guestId={openId} onClose={()=>setOpenId(null)} onChanged={load}/>}
    </div>
  );
}

function NewGuestDialog({ onClose, onDone }) {
  const [f, setF] = useState({
    first_name:"", last_name:"", email:"", phone:"", nationality:"",
    vip_level:"standard", gender:"", dob:"",
    dates: { birthday: "", anniversary: "" },
    company_name: "",
    preferences: {},
    address: {},
  });
  const [saving, setSaving] = useState(false);
  const v = useFormValidation({
    first_name: [required("First name is required")],
    email: [emailV()],
    phone: [phoneV()],
  });
  const submit = async () => {
    if (!v.validateAll(f)) { toast.error("Please fix the highlighted fields"); return; }
    setSaving(true);
    try {
      const body = { ...f };
      if (!body.dates.birthday) delete body.dates.birthday;
      if (!body.dates.anniversary) delete body.dates.anniversary;
      if (!Object.keys(body.dates).length) delete body.dates;
      Object.keys(body).forEach(k => { if (body[k] === "") delete body[k]; });
      const doc = await guestAPI.create(body);
      toast.success("Guest created");
      onDone(doc);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="guest-new-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-[16px] font-semibold text-hocp-navy">New Guest Profile</h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4 text-slate-500"/></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <FormField label="First name" htmlFor="guest-new-first" required error={v.fieldError("first_name")}>
            <input id="guest-new-first" placeholder="e.g. Priya" value={f.first_name} onChange={e=>setF({...f, first_name: e.target.value})} onBlur={()=>v.handleBlur("first_name", f.first_name)} data-testid="guest-new-first" className={cls + " w-full " + errorRing(v.fieldError("first_name"))}/>
          </FormField>
          <FormField label="Last name" htmlFor="guest-new-last" optional>
            <input id="guest-new-last" placeholder="e.g. Sharma" value={f.last_name} onChange={e=>setF({...f, last_name: e.target.value})} data-testid="guest-new-last" className={cls + " w-full"}/>
          </FormField>
          <FormField label="Email" htmlFor="guest-new-email" optional error={v.fieldError("email")}>
            <input id="guest-new-email" placeholder="e.g. guest@example.com" value={f.email} onChange={e=>setF({...f, email: e.target.value})} onBlur={()=>v.handleBlur("email", f.email)} data-testid="guest-new-email" className={cls + " w-full " + errorRing(v.fieldError("email"))}/>
          </FormField>
          <FormField label="Phone" htmlFor="guest-new-phone" optional error={v.fieldError("phone")}>
            <input id="guest-new-phone" placeholder="e.g. +91 98xxxxxx99" value={f.phone} onChange={e=>setF({...f, phone: e.target.value})} onBlur={()=>v.handleBlur("phone", f.phone)} data-testid="guest-new-phone" className={cls + " w-full " + errorRing(v.fieldError("phone"))}/>
          </FormField>
          <FormField label="Nationality" htmlFor="guest-new-nat" optional>
            <input id="guest-new-nat" placeholder="e.g. IN, US" value={f.nationality} onChange={e=>setF({...f, nationality: e.target.value})} data-testid="guest-new-nat" className={cls + " w-full"}/>
          </FormField>
          <FormField label="VIP level" htmlFor="guest-new-vip">
            <select id="guest-new-vip" value={f.vip_level} onChange={e=>setF({...f, vip_level: e.target.value})} data-testid="guest-new-vip" className={cls + " w-full"}>
              <option value="standard">Standard</option><option value="silver">Silver</option>
              <option value="gold">Gold</option><option value="platinum">Platinum</option>
            </select>
          </FormField>
          <FormField label="Gender" htmlFor="guest-new-gender" optional>
            <select id="guest-new-gender" value={f.gender} onChange={e=>setF({...f, gender: e.target.value})} data-testid="guest-new-gender" className={cls + " w-full"}>
              <option value="">Select…</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
            </select>
          </FormField>
          <FormField label="Date of birth" optional>
            <DatePicker value={(f.dob || '').slice(0,10)} onChange={(_v) => setF({...f, dob: _v})} data-testid="guest-new-dob"/>
          </FormField>
          <FormField label="Birthday" optional>
            <DatePicker value={(f.dates.birthday || '').slice(0,10)} onChange={(_v) => setF({...f, dates:{...f.dates, birthday: _v}})}/>
          </FormField>
          <FormField label="Anniversary" optional>
            <DatePicker value={(f.dates.anniversary || '').slice(0,10)} onChange={(_v) => setF({...f, dates:{...f.dates, anniversary: _v}})}/>
          </FormField>
          <FormField label="Company" htmlFor="guest-new-company" optional className="col-span-2">
            <input id="guest-new-company" placeholder="e.g. Acme Corp" value={f.company_name} onChange={e=>setF({...f, company_name: e.target.value})} data-testid="guest-new-company" className={cls + " w-full"}/>
          </FormField>
        </div>
        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2 border-t border-slate-100">
          <button onClick={onClose} className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="guest-new-submit"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4"/> {saving ? "Saving…" : "Create Guest"}
          </button>
        </div>
      </div>
    </div>
  );
}
